import { ReactNode, useCallback } from "react";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import Textarea from "ds/components/Textarea";
import { HookType } from "utils/hooks";
import DragDropListDroppableContainer from "components/DragDropList/DroppableContainer";
import DragDropListDraggableItem from "components/DragDropList/DraggableItem";
import { ColumnId, RenderItemContentProps } from "components/DragDropList/types";
import Card from "ds/components/Card";
import DragDropListDefaultItem from "components/DragDropList/DefaultItem";

import BeforeAfterCommandsEmptyState from "../EmptyState";
import Item from "./Item";
import { useCommandsInput } from "./useCommandsInput";
import styles from "./styles.module.css";

type CommandsProps = {
  type: HookType;
  title: ReactNode;
  commandsIds: string[];
  allTextCommandsById: Record<string, string>;
  onAddCommands: (type: HookType, lines: string[]) => void;
  onRemoveCommand: (type: HookType, ids: string) => void;
  onEditCommand: (type: HookType, id: string, text: string) => void;
  isDropZoneActive: boolean;
  readOnly?: boolean;
  onChangeCallback?: (value: string) => void;
  columnId: ColumnId;
};

const BeforeAfterCommandsList = ({
  type,
  title,
  commandsIds,
  allTextCommandsById,
  onAddCommands,
  onRemoveCommand,
  onEditCommand,
  readOnly,
  onChangeCallback,
  columnId,
  isDropZoneActive,
}: CommandsProps) => {
  const { handleInputKeyDown, handleInputChange, handleAddCommand, input } = useCommandsInput(
    (lines) => onAddCommands(type, lines),
    onChangeCallback
  );

  const renderItemContent = useCallback(
    ({
      id,
      dragging,
      handleRef,
      handleListeners,
      handleAttributes,
      disabled,
    }: RenderItemContentProps<string, Record<string, unknown> | undefined>) => {
      return (
        <DragDropListDefaultItem
          dragging={dragging}
          handleRef={handleRef}
          handleListeners={handleListeners}
          handleAttributes={handleAttributes}
          disabled={disabled}
          titleComponent={
            <Item
              text={allTextCommandsById[id]}
              readOnly={readOnly}
              onRemoveCommand={onRemoveCommand}
              onEditCommand={onEditCommand}
              type={type}
              id={id}
            />
          }
        />
      );
    },
    [allTextCommandsById, onEditCommand, onRemoveCommand, readOnly, type]
  );

  return (
    <Card variant="secondary">
      <DragDropListDroppableContainer
        key={columnId}
        id={columnId}
        items={commandsIds}
        title={title}
      >
        <SortableContext strategy={verticalListSortingStrategy} items={commandsIds}>
          <Box direction="column">
            {commandsIds.map((commandId) => (
              <DragDropListDraggableItem
                key={commandId}
                id={commandId}
                disabled={readOnly}
                className={styles.item}
                renderItemProps={undefined}
                renderItemContent={renderItemContent}
              />
            ))}
            <BeforeAfterCommandsEmptyState
              isDragActive={isDropZoneActive}
              type={type}
              hidden={!!commandsIds.length}
            />
          </Box>
        </SortableContext>
      </DragDropListDroppableContainer>
      {!readOnly && (
        <>
          <Box gap="medium" margin="0 0 small 0" align="start">
            <FormField
              fullWidth
              label="Add a command"
              helperText="Press Enter to add a new command, or Shift + Enter to add a new line"
            >
              {({ ariaInputProps }) => (
                <Box gap="medium" fullWidth>
                  <Textarea
                    {...ariaInputProps}
                    placeholder="Type your command, e.g. ls -la"
                    value={input}
                    minRows={1}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                  />

                  <Button variant="contrast" disabled={!input} onPress={handleAddCommand}>
                    Add
                  </Button>
                </Box>
              )}
            </FormField>
          </Box>
        </>
      )}
    </Card>
  );
};

export default BeforeAfterCommandsList;
